package com.arena_web.ui.components.feed

import androidx.compose.runtime.*
import com.arena_web.data.dto.FeedImageDto
import com.arena_web.data.dto.FeedItemDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.widgets.PhotoLightbox
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

@Composable
fun FeedPostCard(
    item: FeedItemDto,
    onLikeClick: () -> Unit = {},
    onDoubleTapLike: () -> Unit = {},
    onAuthorClick: () -> Unit = {},
    onCommentClick: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    Column(
        modifier
            .fillMaxWidth()
            .sBorderRadius(28)
            .styleModifier {
                // Per Figma 8702:137661 — blue gradient bg (003BAE / 0357E6
                // at 10% alpha) + solid 1px border in the BorderGradient01
                // end colour (rgba(109, 168, 255, 0.10)).
                property("background", "linear-gradient(-320deg, rgba(0, 59, 174, 0.05) , rgba(3, 87, 230, 0.15))")
                property("border", "1px solid rgba(109, 168, 255, 0.05)")
                property("padding", "${s(32)} ${s(40)} ${s(50)}")
            }
            .sGap(18)
    ) {
        // Header: avatar + name + date (+ city if present, + Club badge for club posts)
        Row(
            Modifier.fillMaxWidth().sGap(12),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Avatar — small player card for USER posts when card data is available,
            // round avatar fallback for CLUB posts or when the user has no card yet.
            val showPlayerCard = item.authorType == "USER" && item.cardImageUrl != null
            if (showPlayerCard) {
                Box(
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .onClick { onAuthorClick() }
                ) {
                    PlayerCard(
                        playerName = "",
                        rating = 0,
                        position = "",
                        sport = "football",
                        size = PlayerCardSize.Highlight,
                        cardAssetsBasePath = item.cardAssetsBasePath,
                        cardTextColor = item.cardTextColor,
                        cardFrameUrl = item.cardImageUrl ?: "",
                        avatarUrl = item.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                        hideRating = true,
                        hideShadow = true,
                    )
                }
            } else {
                Box(
                    Modifier
                        .sSize(40).sBorderRadius(20)
                        .styleModifier {
                            property("overflow", "hidden")
                            property("flex-shrink", "0")
                            property("background-color", "#1A2238")
                            property("cursor", "pointer")
                        }
                        .onClick { onAuthorClick() },
                    contentAlignment = Alignment.Center
                ) {
                    if (item.authorAvatarUrl != null) {
                        val initials = item.authorName.take(1).uppercase()
                        Div(attrs = {
                            ref { el ->
                                el.innerHTML = "<img src=\"${item.authorAvatarUrl}\" referrerpolicy=\"no-referrer\" " +
                                    "onerror=\"this.style.display='none';this.nextSibling.style.display='flex'\" " +
                                    "style=\"width:100%;height:100%;object-fit:cover;\" />" +
                                    "<span style=\"display:none;width:100%;height:100%;align-items:center;justify-content:center;" +
                                    "font-size:${s(16)};font-weight:500;color:#B4CCF6;\">$initials</span>"
                                onDispose {}
                            }
                            style { property("width", "100%"); property("height", "100%") }
                        })
                    } else {
                        SpanText(
                            item.authorName.take(1).uppercase(),
                            modifier = Modifier
                                .sFontSize(16)
                                .fontWeight(AppTypography.Medium)
                                .color(Color("var(--arena-text-1)"))
                        )
                    }
                }
            }

            // Name + (city, if any) + date stack
            Column(Modifier.flexGrow(1).styleModifier { property("gap", "6px") }) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.sGap(8)
                ) {
                    SpanText(
                        item.authorName,
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(18)
                            .color(com.arena_web.ui.theme.ThemeVars.Text1)
                            .cursor(Cursor.Pointer)
                            .onClick { onAuthorClick() }
                    )
                    Box(
                        modifier = Modifier
                            .sSize(3)
                            .sBorderRadius(100)
                            .backgroundColor(AppColors.Text.Muted)
                    )
                    SpanText(
                        formatFeedDate(item.createdAt),
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(24)
                            .sLetterSpacing(0.16)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
                if (!item.city.isNullOrBlank()) {
                    SpanText(
                        item.city!!,
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(AppTypography.Light)
                            .sLineHeight(18)
                            .sLetterSpacing(0.028)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
            }

            // Author type badge
            if (item.authorType == "CLUB") {
                Box(
                    Modifier
                        .sBorderRadius(8).sPadding(4, 8)
                        .styleModifier {
                            property("background-color", "rgba(0, 97, 255, 0.15)")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        t.feedClubBadge,
                        modifier = Modifier
                            .sFontSize(12)
                            .color(Color("#5B9CFF"))
                    )
                }
            }
        }

        // Content text
        if (item.content.isNotBlank()) {
            SpanText(
                item.content,
                modifier = Modifier
                    .fillMaxWidth()
                    .sFontSize(16)
                    .color(Color("var(--arena-text-2)"))
                    .styleModifier { property("white-space", "pre-wrap") }
            )
        }

        // Image grid — double-tap anywhere on it to like (Instagram-style),
        // single click on a photo opens the fullscreen viewer at that photo.
        if (item.images.isNotEmpty()) {
            var viewerIndex by remember(item.id) { mutableStateOf<Int?>(null) }
            DoubleTapLikeArea(
                onDoubleTapLike = onDoubleTapLike,
                modifier = Modifier.fillMaxWidth(),
                onImageTap = { idx -> viewerIndex = idx },
            ) {
                PostImageGrid(item.images, item.layout)
            }
            viewerIndex?.let { idx ->
                PhotoLightbox(
                    urls = item.images.map { it.imageUrl },
                    initialIndex = idx,
                    onDismiss = { viewerIndex = null },
                )
            }
        }

        // Like + comment row — per Figma 8702:137692, 18px gap between the
        // like cluster and the comment cluster, 8px gap within each cluster.
        Row(
            Modifier.sGap(18),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Like cluster
            Row(
                Modifier.sGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Image(
                    src = AppIcons.Heart,
                    modifier = Modifier
                        .sSize(24)
                        .cursor(Cursor.Pointer)
                        .styleModifier {
                            if (item.isLikedByMe) {
                                property("filter", "brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(4997%) hue-rotate(344deg) brightness(95%) contrast(99%)")
                            } else {
                                property("--fill-0", "#B4CCF6")
                            }
                        }
                        .onClick { onLikeClick() }
                )
                SpanText(
                    "${item.likesCount}",
                    modifier = Modifier
                        .sFontSize(18)
                        .color(Color("var(--arena-text-2)"))
                )
            }

            // Comment cluster — icon + count, mirrors the like cluster.
            Row(
                Modifier
                    .sGap(8)
                    .cursor(Cursor.Pointer)
                    .onClick { onCommentClick() },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Image(
                    src = AppIcons.Chat,
                    modifier = Modifier
                        .sSize(24)
                        .styleModifier { property("--fill-0", "#B4CCF6") }
                )
                SpanText(
                    "${item.commentsCount}",
                    modifier = Modifier
                        .sFontSize(18)
                        .color(Color("var(--arena-text-2)"))
                )
            }
        }
    }
}

@Composable
private fun PostImageGrid(images: List<FeedImageDto>, layout: Int) {
    val gap = s(12)
    val radius = s(12)

    // Mirror PlayerHubSection.PostImageCollage so feed and profile render the same
    // dimensions per layout.
    val resolved = layout

    val slotFillMod = Modifier.flexGrow(1).fillMaxHeight().styleModifier {
        property("flex-basis", "0")
        property("min-width", "0")
    }
    val rowFillMod = Modifier.fillMaxWidth().flexGrow(1).styleModifier {
        property("flex-basis", "0")
        property("min-height", "0")
    }
    val urls = images.map { it.imageUrl }
    when (resolved) {
        // Layout 1 — single portrait. 3:4 aspect, centered, 75% width.
        1 -> Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center) {
            FeedImageFill(
                urls[0],
                radius = radius,
                modifier = Modifier.fillMaxWidth(75.percent).styleModifier { property("aspect-ratio", "3 / 4") }
            )
        }
        // Layout 2 — single landscape, 16:9.
        2 -> FeedImageFill(
            urls[0],
            radius = radius,
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "16 / 9") }
        )
        // Layout 3 — 2 portraits side-by-side, outer 4:3.
        3 -> Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        ) {
            Row(modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }) {
                FeedImageFill(urls[0], radius = radius, modifier = slotFillMod)
                if (urls.size >= 2) FeedImageFill(urls[1], radius = radius, modifier = slotFillMod)
            }
        }
        // Layout 4 — 2 landscape stacked, outer 4:3.
        4 -> Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        ) {
            Column(modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }) {
                FeedImageFill(urls[0], radius = radius, modifier = rowFillMod)
                if (urls.size >= 2) FeedImageFill(urls[1], radius = radius, modifier = rowFillMod)
            }
        }
        // Layout 5 — wide top + 2 below, outer 4:3 (rows 196:150).
        5 -> Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        ) {
            Column(modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }) {
                FeedImageFill(
                    urls[0],
                    radius = radius,
                    modifier = Modifier.fillMaxWidth().styleModifier {
                        property("flex", "196 0 0")
                        property("min-height", "0")
                    }
                )
                if (urls.size >= 2) Row(
                    modifier = Modifier.fillMaxWidth().styleModifier {
                        property("flex", "150 0 0")
                        property("min-height", "0")
                        property("gap", gap)
                    }
                ) {
                    FeedImageFill(urls[1], radius = radius, modifier = slotFillMod)
                    if (urls.size >= 3) FeedImageFill(urls[2], radius = radius, modifier = slotFillMod)
                }
            }
        }
        // Layout 6 — 2 top + wide below, outer 4:3 (rows 150:196).
        6 -> Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        ) {
            Column(modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }) {
                if (urls.size >= 2) Row(
                    modifier = Modifier.fillMaxWidth().styleModifier {
                        property("flex", "150 0 0")
                        property("min-height", "0")
                        property("gap", gap)
                    }
                ) {
                    FeedImageFill(urls[0], radius = radius, modifier = slotFillMod)
                    FeedImageFill(urls[1], radius = radius, modifier = slotFillMod)
                }
                if (urls.size >= 3) FeedImageFill(
                    urls[2],
                    radius = radius,
                    modifier = Modifier.fillMaxWidth().styleModifier {
                        property("flex", "196 0 0")
                        property("min-height", "0")
                    }
                )
            }
        }
        else -> FeedImageFill(
            urls[0],
            radius = radius,
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        )
    }
}

@Composable
private fun FeedImageFill(url: String, radius: String, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .styleModifier {
                property("border-radius", radius)
                property("overflow", "hidden")
                property("background-image", "url('$url')")
                property("background-size", "cover")
                property("background-position", "center")
                property("background-repeat", "no-repeat")
            }
    )
}

private fun formatFeedDate(isoDate: String): String {
    return try {
        val date = kotlin.js.Date(isoDate)
        val now = kotlin.js.Date()
        val diffMs = now.getTime() - date.getTime()
        val diffMin = (diffMs / 60000).toLong()
        val diffHours = (diffMs / 3600000).toLong()
        val diffDays = (diffMs / 86400000).toLong()

        when {
            diffMin < 1 -> "Just now"
            diffMin < 60 -> "${diffMin}m ago"
            diffHours < 24 -> "${diffHours}h ago"
            diffDays < 7 -> "${diffDays}d ago"
            else -> {
                val day = date.getDate()
                val month = date.toLocaleString("en", js("({month:'short'})")).toString()
                val year = date.getFullYear()
                val currentYear = now.getFullYear()
                if (year == currentYear) "$day $month" else "$day $month $year"
            }
        }
    } catch (_: Exception) {
        isoDate
    }
}
